/* whero-flavor-vote.js
   --------------------------------------------------------------
   Handles the flavor-vote section's form + client-fetched archive grid.

   Behavior:
     - Form submit POSTs JSON straight to the Vercel bridge (cross-origin,
       CORS-allowlisted) with a per-browser voter token from localStorage —
       the bridge rate-limits on that token. Routing through the Shopify app
       proxy instead makes Vercel see Shopify's egress IP for every shopper
       and the per-IP limit rejects honest voters (Sept 2026 bug).
     - (Legacy) /apps/api/vote (same-origin; Shopify app proxy
       signs the request to Vercel).
     - Submit button is disabled for 3s after click to throttle double-
       clicks; the fetch will also ignore concurrent submits.
     - On 200 { ok: true }: replace form with success block, focus it.
     - On 200 { ok: false, error }: show inline error, re-enable submit.
     - Honeypot field: filled-in submissions are rejected server-side
       with a fake-success response; we render the same success UI so
       the abuser can't tell.

   Archive grid (client-fetched because shop.metaobjects.*.values caps
   at 50 entries in Liquid and the archive is expected to exceed that):
     - Paginates GET /apps/api/vote/archive (?cursor=...) until cursor is
       null or a 500-entry safety cap is hit, concatenating results.
     - Replaces server-rendered skeletons with real cards, preserving
       the canonical whero-flavor-grid-card class surface.
     - 404 → empty state (endpoint not yet deployed; graceful).
     - Other errors → error state with refresh button.
     - Pick-button handler is event-delegated from the mount point so
       newly rendered cards work without re-binding.

   Dietary filters (chips above the grid):
     - Exclude-semantics: an active chip HIDES any flavor whose allergen
       codes match it. Codes arrive from Toast via the bridge as an
       uppercase JSON array, e.g. ["MILK","WHEAT","ALMOND"].
     - "Gluten Free" keys off WHEAT by policy: every gluten-carrying
       item gets flagged WHEAT in Toast, and the guest-facing label
       matches site messaging (per Chris, Jul 2026).
     - Flavors with no allergen data pass every filter (an absent list
       means "none recorded", e.g. sorbets).
     - The bar is skipped entirely when the section toggle is off or
       when no fetched flavor carries allergen data (chips that can't
       do anything read as broken).
*/
(function () {
  'use strict';

  var ARCHIVE_ENDPOINT = '/apps/api/vote/archive';
  var ARCHIVE_MAX = 500;

  document.querySelectorAll('[data-whero-flavor-vote]').forEach(function (section) {
    var form = section.querySelector('[data-whero-vote-form]');
    var successEl = section.querySelector('[data-whero-vote-success]');
    var errorEl = section.querySelector('[data-whero-vote-error]');
    var input = section.querySelector('[data-whero-vote-input]');
    var submitBtn = section.querySelector('[data-whero-vote-submit]');
    var tsInput = section.querySelector('[data-whero-vote-ts]');
    var errorCopyEl = section.querySelector('[data-whero-vote-error-copy]');
    var archiveMount = section.querySelector('[data-whero-flavor-vote-archive]');

    var defaultError = 'Something went wrong. Try again in a moment.';
    if (errorCopyEl) {
      try { defaultError = JSON.parse(errorCopyEl.textContent) || defaultError; }
      catch (_) {}
    }

    // ---------- archive grid ----------
    if (archiveMount) {
      hydrateArchive(section, archiveMount);
    }

    // Delegated pick-button handler: works for both server-rendered
    // (legacy) and client-rendered cards. Scoped to the section so a
    // second vote section on the page (unlikely, but possible) can't
    // poach clicks.
    section.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest && e.target.closest('[data-whero-vote-pick]');
      if (!btn || !section.contains(btn)) return;
      e.preventDefault();
      e.stopPropagation();
      var name = btn.getAttribute('data-flavor-name') || '';
      if (!input) return;
      input.value = name;
      input.focus({ preventScroll: true });
      if (form && form.scrollIntoView) {
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });

    section.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var btn = e.target && e.target.closest && e.target.closest('[data-whero-vote-pick]');
      if (!btn || !section.contains(btn)) return;
      // Don't let the keypress bubble to any parent that might re-handle.
      e.stopPropagation();
    });

    if (!form) return;

    var submitting = false;
    var cooldownUntil = 0;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitting) return;
      if (Date.now() < cooldownUntil) return;

      if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

      var raw = (input && input.value || '').trim();
      if (!raw) {
        showError('Type a flavor first.');
        return;
      }
      if (raw.length > 80) {
        showError('Keep it to 80 characters or fewer.');
        return;
      }

      submitting = true;
      cooldownUntil = Date.now() + 3000;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.setAttribute('aria-busy', 'true');
      }
      if (tsInput) tsInput.value = String(Date.now());

      var data = new FormData(form);
      var payload = {
        flavor: String(data.get('flavor') || ''),
        source: String(data.get('source') || 'web'),
        website: String(data.get('website') || ''),
        submitted_at: Number(data.get('submitted_at') || Date.now()),
        voter_token: voterToken()
      };

      fetch(form.getAttribute('action') || '/apps/api/vote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload),
        mode: 'cors',
        credentials: 'omit'
      }).then(function (res) {
        return res.json().catch(function () { return { ok: false, error: 'bad_response' }; });
      }).then(function (body) {
        submitting = false;
        if (submitBtn) {
          submitBtn.removeAttribute('aria-busy');
          // Re-enable after the 3s cooldown in case of error.
          setTimeout(function () { submitBtn.disabled = false; }, 3000);
        }

        if (body && body.ok) {
          renderSuccess();
        } else {
          var code = body && body.error ? String(body.error) : '';
          showError(friendlyError(code));
        }
      }).catch(function () {
        submitting = false;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.removeAttribute('aria-busy');
        }
        showError(defaultError);
      });
    });

    function renderSuccess() {
      if (form) form.hidden = true;
      if (successEl) {
        successEl.hidden = false;
        try { successEl.focus(); } catch (_) {}
      }
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg || defaultError;
      errorEl.hidden = false;
    }

    function friendlyError(code) {
      switch (code) {
        case 'rate_limited':
          return "You've voted a few times today — come back tomorrow.";
        case 'empty':
        case 'too_long':
          return 'Keep it to a flavor name, 80 characters or fewer.';
        case 'invalid':
        case 'links_not_allowed':
          return 'That doesn\u2019t look like a flavor name.';
        default:
          return defaultError;
      }
    }
  });

  // ---------- voter token ----------

  // Random per-browser id, minted once and kept in localStorage. The bridge
  // hashes it (with a server pepper) and applies the "3 votes / 24h" limit
  // to the hash, so shoppers sharing an IP (household, office, carrier NAT)
  // don't collide. If storage is unavailable we mint a fresh token per
  // page load and the bridge's per-IP backstop still applies.
  var VOTER_TOKEN_KEY = 'whero_voter_token';
  var VOTER_TOKEN_RE = /^[A-Za-z0-9_-]{16,64}$/;
  var sessionToken = null;

  function voterToken() {
    var t = null;
    try { t = window.localStorage.getItem(VOTER_TOKEN_KEY); } catch (_) {}
    if (t && VOTER_TOKEN_RE.test(t)) return t;
    if (!sessionToken) sessionToken = randomToken();
    try { window.localStorage.setItem(VOTER_TOKEN_KEY, sessionToken); } catch (_) {}
    return sessionToken;
  }

  function randomToken() {
    var bytes = new Array(24);
    var i;
    try {
      var buf = new Uint8Array(24);
      window.crypto.getRandomValues(buf);
      for (i = 0; i < 24; i++) bytes[i] = buf[i];
    } catch (_) {
      for (i = 0; i < 24; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    var hex = '';
    for (i = 0; i < 24; i++) hex += (bytes[i] < 16 ? '0' : '') + bytes[i].toString(16);
    return hex; // 48 hex chars
  }

  // ---------- archive grid helpers ----------

  function hydrateArchive(section, mount) {
    var pickLabel = mount.getAttribute('data-pick-label') || 'Vote for this →';

    fetchArchive().then(function (result) {
      if (result.notDeployed) {
        swapTemplate(section, mount, 'empty');
        return;
      }
      var flavors = result.flavors || [];
      if (flavors.length === 0) {
        swapTemplate(section, mount, 'empty');
        return;
      }
      // `filters` is assigned before any chip can be toggled, so the
      // callback closing over it never fires while it's still undefined.
      var filters = buildFilterBar(mount, flavors, function (filtered) {
        if (filtered.length === 0) {
          renderFilterEmpty(mount, function () { filters.clear(); });
        } else {
          renderCards(mount, filtered, pickLabel);
        }
      });
      renderCards(mount, flavors, pickLabel);
    }).catch(function (err) {
      try { console.warn('[flavor-vote] archive hydrate failed:', err); } catch (_) {}
      swapTemplate(section, mount, 'error');
    });

    // Retry button inside the error template reloads the page. Wire
    // it up after a swap (can't bind until the template is in the DOM).
    section.addEventListener('click', function (e) {
      var retry = e.target && e.target.closest && e.target.closest('[data-whero-flavor-vote-error-retry]');
      if (!retry || !section.contains(retry)) return;
      e.preventDefault();
      try { location.reload(); } catch (_) {}
    });
  }

  function fetchArchive() {
    var all = [];
    var safetyCap = ARCHIVE_MAX;

    function page(cursor) {
      var url = ARCHIVE_ENDPOINT;
      if (cursor) {
        url += (url.indexOf('?') === -1 ? '?' : '&') + 'cursor=' + encodeURIComponent(cursor);
      }
      return fetch(url, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      }).then(function (res) {
        if (res.status === 404) {
          // Endpoint not deployed yet — caller treats as empty state.
          return { notDeployed: true };
        }
        if (!res.ok) {
          throw new Error('archive_http_' + res.status);
        }
        return res.json().catch(function () {
          throw new Error('archive_bad_json');
        });
      }).then(function (body) {
        if (body && body.notDeployed) return { notDeployed: true };
        if (!body || !Array.isArray(body.flavors)) {
          throw new Error('archive_bad_shape');
        }
        for (var i = 0; i < body.flavors.length && all.length < safetyCap; i++) {
          all.push(body.flavors[i]);
        }
        var next = body.cursor;
        if (next && all.length < safetyCap) {
          return page(next);
        }
        return { flavors: all };
      });
    }

    return page(null);
  }

  function swapTemplate(section, mount, kind) {
    var sel = kind === 'empty'
      ? '[data-whero-flavor-vote-empty]'
      : '[data-whero-flavor-vote-error]';
    var tpl = section.querySelector(sel);
    mount.innerHTML = '';
    mount.setAttribute('aria-busy', 'false');
    if (!tpl || !tpl.content) return;
    mount.appendChild(tpl.content.cloneNode(true));
  }

  // ---------- dietary filters ----------

  // Tree-nut + peanut codes seen in Toast data, plus a suffix catch-all
  // below for codes we haven't seen yet. Deliberately conservative: an
  // over-match only hides a flavor someone could actually eat; an
  // under-match shows an allergen flavor to someone filtering it out.
  var NUT_CODES = {
    PEANUT: 1, PEANUTS: 1, ALMOND: 1, ALMONDS: 1, WALNUT: 1, WALNUTS: 1,
    PECAN: 1, PECANS: 1, PISTACHIO: 1, PISTACHIOS: 1, MACADAMIA: 1,
    CASHEW: 1, CASHEWS: 1, HAZELNUT: 1, HAZELNUTS: 1, CHESTNUT: 1,
    COCONUT: 1, BRAZIL_NUT: 1, PINE_NUT: 1,
    TREE_NUT: 1, TREE_NUTS: 1, TREENUT: 1, TREENUTS: 1, NUT: 1, NUTS: 1
  };

  // Exclude-semantics: an active chip hides flavors whose allergen codes
  // match. "Gluten Free" = WHEAT by policy (all gluten items are flagged
  // WHEAT in Toast); "Hold the Cow" mirrors the site's dairy-free label.
  var DIETARY_FILTERS = [
    { key: 'gluten-free',  label: 'Gluten Free',  excludes: function (c) { return c === 'WHEAT' || c === 'GLUTEN'; } },
    { key: 'hold-the-cow', label: 'Hold the Cow', excludes: function (c) { return c === 'MILK' || c === 'DAIRY' || c === 'LACTOSE'; } },
    { key: 'nut-free',     label: 'Nut Free',     excludes: function (c) { return NUT_CODES[c] === 1 || /NUTS?$/.test(c); } },
    { key: 'egg-free',     label: 'No Egg',       excludes: function (c) { return c === 'EGG' || c === 'EGGS'; } },
    { key: 'soy-free',     label: 'No Soy',       excludes: function (c) { return c === 'SOY' || c === 'SOYA' || c === 'SOYBEAN'; } }
  ];

  function normalizeAllergenCode(raw) {
    return String(raw || '').toUpperCase().trim().replace(/[\s-]+/g, '_');
  }

  // Returns the flavor's normalized allergen codes. The archive API
  // sends a parsed array; tolerate a raw JSON string in case an older
  // deploy (or the Liquid-rendered legacy path) hands one through.
  function flavorAllergens(flavor) {
    var raw = flavor && flavor.allergens;
    if (typeof raw === 'string') {
      try { raw = JSON.parse(raw); } catch (_) { return []; }
    }
    if (!Array.isArray(raw)) return [];
    var out = [];
    for (var i = 0; i < raw.length; i++) {
      var code = normalizeAllergenCode(raw[i]);
      if (code) out.push(code);
    }
    return out;
  }

  // Positive diet badges, mirroring whero-flavor-grid-card.liquid's
  // logic (vegan implies dairy-free, never both; skipped on dirty-sodas
  // where every soda is technically vegan/GF and the badges add noise).
  // Same classes as the Liquid snippet, so whero-flavor-grid.css — which
  // this page already loads — paints them identically to other screens.
  function buildDietBadges(flavor) {
    if (normalizeCategory(flavor && flavor.category) === 'dirty-sodas') return null;

    var joined = flavorAllergens(flavor).join(' ');
    var hasDairy = joined.indexOf('DAIRY') !== -1 || joined.indexOf('MILK') !== -1;
    var hasEggs = joined.indexOf('EGG') !== -1;
    var hasGluten = joined.indexOf('WHEAT') !== -1 || joined.indexOf('GLUTEN') !== -1;

    var badges = [];
    if (!hasDairy && !hasEggs) badges.push(['vegan', '🌿', 'Vegan']);
    else if (!hasDairy) badges.push(['df', '🌱', 'Dairy-Free']);
    if (!hasGluten) badges.push(['gf', '🌾', 'Gluten-Free']);
    if (badges.length === 0) return null;

    var wrap = document.createElement('div');
    wrap.className = 'whero-flavor-card__diet';
    for (var i = 0; i < badges.length; i++) {
      var span = document.createElement('span');
      span.className = 'whero-diet-badge whero-diet-badge--' + badges[i][0];
      var icon = document.createElement('span');
      icon.className = 'whero-diet-badge__icon';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = badges[i][1];
      span.appendChild(icon);
      span.appendChild(document.createTextNode(badges[i][2]));
      wrap.appendChild(span);
    }
    return wrap;
  }

  // Builds the chip bar and inserts it before the grid mount. Returns
  // { apply, clear } for the caller, or null when filters are disabled
  // or pointless (no flavor in the payload carries allergen data).
  function buildFilterBar(mount, flavors, onChange) {
    if (mount.getAttribute('data-filters') === '0') return null;
    if (!mount.parentNode) return null;

    var anyAllergens = false;
    for (var i = 0; i < flavors.length; i++) {
      if (flavorAllergens(flavors[i]).length > 0) { anyAllergens = true; break; }
    }
    if (!anyAllergens) return null;

    var active = {};

    var bar = document.createElement('div');
    bar.className = 'whero-flavor-vote__filters';

    var chips = document.createElement('div');
    chips.className = 'whero-flavor-vote__filter-chips';
    chips.setAttribute('role', 'group');
    chips.setAttribute('aria-label', 'Dietary filters');

    DIETARY_FILTERS.forEach(function (filter) {
      var chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'whero-flavor-vote__filter-chip';
      chip.setAttribute('aria-pressed', 'false');
      chip.setAttribute('data-filter-key', filter.key);
      chip.textContent = filter.label;
      chip.addEventListener('click', function () {
        var on = !active[filter.key];
        if (on) active[filter.key] = true;
        else delete active[filter.key];
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        emit();
      });
      chips.appendChild(chip);
    });
    bar.appendChild(chips);

    // Count is aria-live so toggling a chip announces the new result
    // size to screen readers without moving focus off the chip.
    var count = document.createElement('p');
    count.className = 'whero-flavor-vote__filter-count';
    count.setAttribute('aria-live', 'polite');
    count.hidden = true;
    bar.appendChild(count);

    var note = mount.getAttribute('data-filters-note') || '';
    if (note) {
      var noteEl = document.createElement('p');
      noteEl.className = 'whero-flavor-vote__filter-note';
      noteEl.textContent = note;
      bar.appendChild(noteEl);
    }

    mount.parentNode.insertBefore(bar, mount);

    function activeFilters() {
      var list = [];
      for (var i = 0; i < DIETARY_FILTERS.length; i++) {
        if (active[DIETARY_FILTERS[i].key]) list.push(DIETARY_FILTERS[i]);
      }
      return list;
    }

    function apply(list) {
      var current = activeFilters();
      if (current.length === 0) return list;
      return list.filter(function (flavor) {
        var codes = flavorAllergens(flavor);
        for (var i = 0; i < current.length; i++) {
          for (var j = 0; j < codes.length; j++) {
            if (current[i].excludes(codes[j])) return false;
          }
        }
        return true;
      });
    }

    function emit() {
      var filtered = apply(flavors);
      var anyActive = activeFilters().length > 0;
      count.hidden = !anyActive;
      if (anyActive) {
        count.textContent = 'Showing ' + filtered.length + ' of ' + flavors.length + ' flavors';
      }
      onChange(filtered);
    }

    function clear() {
      active = {};
      chips.querySelectorAll('.whero-flavor-vote__filter-chip').forEach(function (c) {
        c.setAttribute('aria-pressed', 'false');
      });
      emit();
    }

    return { apply: apply, clear: clear };
  }

  // Every card filtered out: keep the (JS-built) chip bar in place and
  // swap the grid for a friendly nudge. Distinct from the Liquid empty
  // template, which means "the archive itself is empty".
  function renderFilterEmpty(mount, onClear) {
    mount.innerHTML = '';
    mount.removeAttribute('data-grouped');
    mount.setAttribute('aria-busy', 'false');

    var wrap = document.createElement('div');
    wrap.className = 'whero-flavor-vote__archive-empty whero-flavor-vote__archive-empty--filtered';

    var p = document.createElement('p');
    p.textContent = 'Nothing matches those filters — clear one and keep browsing.';
    wrap.appendChild(p);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'whero-btn whero-btn--secondary';
    btn.textContent = 'Clear filters';
    btn.addEventListener('click', onClear);
    wrap.appendChild(btn);

    mount.appendChild(wrap);
  }

  // Categories mirror /pages/current-flavors. Order is canonical and
  // unrecognized categories (or missing values) bucket into "other".
  var ARCHIVE_CATEGORIES = [
    { key: 'always',       label: 'Always' },
    { key: 'sometimes',    label: 'Sometimes' },
    { key: 'hold-the-cow', label: 'Hold the Cow' },
    { key: 'dirty-sodas',  label: 'Dirty Sodas & Floats' },
    { key: 'other',        label: 'Other' }
  ];

  function normalizeCategory(raw) {
    if (typeof raw !== 'string') return '';
    return raw.toLowerCase().trim().replace(/\s+/g, '-');
  }

  // Read once per render — driven by the section's data attribute (theme
  // editor toggle) AND the presence of Klaviyo creds. If creds are absent,
  // skip the disclosure entirely so a sold-out card can't show a form
  // that does nothing on submit. Aligns with the snippet's same check.
  function notifyMeEnabledFor(mount) {
    var section = mount.closest('[data-whero-flavor-vote]');
    var sectionToggle = section && section.getAttribute('data-archive-notify-me') !== '0';
    var k = window.WHERO_KLAVIYO || {};
    return !!(sectionToggle && k.publicKey && k.restockListId);
  }

  function buildNotifyMe(flavor) {
    var name = flavor && flavor.name ? String(flavor.name) : '';
    if (!name) return null;
    // Mirror Shopify's handleize so the slug matches whatever the
    // notify-me snippet would have produced on a PDP / flavor grid card.
    // Apostrophes must be STRIPPED (not replaced with `-`) — handleize
    // emits "wizards" not "wizard-s" for "Wizard's". Restocked alerts
    // are persisted in localStorage by slug, so a divergent slug here
    // would mean a user who subscribed on /pages/current-flavors gets
    // re-prompted here instead of seeing the "you're on the list" state.
    var slug = (flavor.slug && String(flavor.slug)) ||
               name.toLowerCase().trim()
                   .replace(/['’]/g, '')
                   .replace(/[^a-z0-9]+/g, '-')
                   .replace(/^-+|-+$/g, '');
    var uid = 'vote-' + slug + '-' + Math.random().toString(36).slice(2, 8);

    var details = document.createElement('details');
    details.className = 'whero-flavor-vote__notify-me';

    var summary = document.createElement('summary');
    summary.className = 'whero-flavor-vote__notify-toggle';
    summary.textContent = "🔔 Notify me when it's back";
    details.appendChild(summary);

    var root = document.createElement('div');
    root.className = 'whero-notify whero-notify--inline';
    root.setAttribute('data-whero-notify', '');
    root.setAttribute('data-flavor-slug', slug);
    root.setAttribute('data-flavor-name', name);
    root.setAttribute('data-source', 'vote-archive');

    var form = document.createElement('form');
    form.className = 'whero-notify__form';
    form.setAttribute('data-whero-notify-form', '');
    form.setAttribute('novalidate', '');
    form.setAttribute('aria-describedby', 'whero-notify-status-' + uid);

    var fs = document.createElement('fieldset');
    fs.className = 'whero-notify__fieldset';

    var legend = document.createElement('legend');
    legend.className = 'visually-hidden';
    legend.textContent = 'Restock alert for ' + name;
    fs.appendChild(legend);

    var label = document.createElement('label');
    label.className = 'whero-notify__title';
    label.setAttribute('for', 'whero-notify-email-' + uid);
    label.textContent = "Email me when this is back";
    fs.appendChild(label);

    function hidden(n, v) {
      var i = document.createElement('input');
      i.type = 'hidden'; i.name = n; i.value = v;
      return i;
    }
    fs.appendChild(hidden('flavor_slug', slug));
    fs.appendChild(hidden('flavor_name', name));

    var field = document.createElement('div');
    field.className = 'whero-notify__field';
    var email = document.createElement('input');
    email.id = 'whero-notify-email-' + uid;
    email.className = 'whero-notify__input';
    email.type = 'email';
    email.name = 'email';
    email.required = true;
    email.autocomplete = 'email';
    email.setAttribute('inputmode', 'email');
    email.placeholder = 'you@email.com';
    email.setAttribute('aria-describedby', 'whero-notify-status-' + uid);
    field.appendChild(email);
    fs.appendChild(field);

    var submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'whero-notify__submit';
    submit.textContent = "Tell me when it's back →";
    fs.appendChild(submit);

    var status = document.createElement('div');
    status.id = 'whero-notify-status-' + uid;
    status.className = 'whero-notify__status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    status.setAttribute('data-whero-notify-status', '');
    status.hidden = true;
    fs.appendChild(status);

    form.appendChild(fs);
    root.appendChild(form);

    var success = document.createElement('div');
    success.className = 'whero-notify__success';
    success.setAttribute('data-whero-notify-success', '');
    success.setAttribute('role', 'status');
    success.setAttribute('aria-live', 'polite');
    success.hidden = true;
    var sTitle = document.createElement('p');
    sTitle.className = 'whero-notify__success-title';
    sTitle.textContent = "You're on the list.";
    var sBody = document.createElement('p');
    sBody.className = 'whero-notify__success-body';
    sBody.textContent = "We'll send a note the minute " + name + " rotates back into the case.";
    success.appendChild(sTitle);
    success.appendChild(sBody);
    root.appendChild(success);

    details.appendChild(root);
    return details;
  }

  function renderCards(mount, flavors, pickLabel) {
    var includeNotifyMe = notifyMeEnabledFor(mount);

    // Bucket flavors by category. Anything that doesn't match a known
    // category falls into "other".
    var known = {};
    for (var c = 0; c < ARCHIVE_CATEGORIES.length; c++) known[ARCHIVE_CATEGORIES[c].key] = true;

    var groups = {};
    for (var i = 0; i < flavors.length; i++) {
      var f = flavors[i];
      var key = normalizeCategory(f && f.category);
      if (!known[key]) key = 'other';
      (groups[key] || (groups[key] = [])).push(f);
    }

    // Graceful fallback: if every flavor lacks a recognized category
    // (e.g., archive API hasn't started returning category yet),
    // render a single flat grid with no category heading — same look
    // as before this change.
    var presentKeys = Object.keys(groups);
    var ungrouped = presentKeys.length === 1 && presentKeys[0] === 'other';

    var frag = document.createDocumentFragment();

    if (ungrouped) {
      for (var u = 0; u < flavors.length; u++) {
        var node = buildCard(flavors[u], pickLabel, includeNotifyMe);
        if (node) frag.appendChild(node);
      }
      mount.removeAttribute('data-grouped');
    } else {
      for (var k = 0; k < ARCHIVE_CATEGORIES.length; k++) {
        var cat = ARCHIVE_CATEGORIES[k];
        var list = groups[cat.key];
        if (!list || list.length === 0) continue;

        var sec = document.createElement('section');
        sec.className = 'whero-flavor-vote__archive-cat';
        sec.setAttribute('data-cat', cat.key);

        var h3 = document.createElement('h3');
        h3.className = 'whero-flavor-vote__archive-cat-heading';
        h3.textContent = cat.label;
        sec.appendChild(h3);

        var listEl = document.createElement('div');
        listEl.className = 'whero-flavor-vote__archive-cat-list';
        listEl.setAttribute('role', 'list');
        for (var m = 0; m < list.length; m++) {
          var card = buildCard(list[m], pickLabel, includeNotifyMe);
          if (card) listEl.appendChild(card);
        }
        sec.appendChild(listEl);
        frag.appendChild(sec);
      }
      mount.setAttribute('data-grouped', 'true');
    }

    mount.innerHTML = '';
    mount.appendChild(frag);
    mount.setAttribute('aria-busy', 'false');

    // Reveal the Read more toggle on any description the CSS line-clamp is
    // actually cutting off. Must run post-mount: overflow only exists once
    // the cards are laid out in the document (buildCard works on detached
    // nodes). scrollHeight > clientHeight means there's hidden text worth a
    // toggle; descriptions that fit within the clamp keep theirs hidden.
    mount.querySelectorAll('.whero-flavor-card__desc').forEach(function (p) {
      if (p.scrollHeight - p.clientHeight > 1) {
        var btn = p.nextElementSibling;
        if (btn && btn.classList.contains('whero-flavor-card__readmore')) {
          btn.hidden = false;
        }
      }
    });

    // Wire any [data-whero-notify] nodes we just inserted. notify-me.js's
    // init() already ran on DOMContentLoaded (before the archive fetch
    // resolved), so without this poke the new forms would do nothing on
    // submit. wire() is idempotent — re-running on the page is safe.
    if (includeNotifyMe && typeof window.wheroNotifyMeInit === 'function') {
      window.wheroNotifyMeInit();
    }
  }

  // Hand-translated from snippets/whero-flavor-grid-card.liquid.
  // Class names and structure mirror the Liquid snippet so archive
  // cards share the same CSS surface as the rest of the site's cards.
  // Per-shop urgency (pill/strip), modal dialog (sizes), and flair are
  // omitted because the archive response doesn't carry that data —
  // archived flavors aren't in rotation. Allergens ARE in the response
  // and drive the same positive diet badges (Vegan / Dairy-Free /
  // Gluten-Free) the site's other flavor cards show.
  function buildCard(flavor, pickLabel, includeNotifyMe) {
    if (!flavor || !flavor.name) return null;

    var item = document.createElement('div');
    item.className = 'whero-flavor-vote__archive-item';
    item.setAttribute('role', 'listitem');

    var gridItem = document.createElement('div');
    gridItem.className = 'whero-flavor-grid__item';

    var article = document.createElement('article');
    article.className = 'whero-flavor-card';

    var imgWrap = document.createElement('div');
    imgWrap.className = 'whero-flavor-card__img';

    var imgUrl = typeof flavor.image_url === 'string' ? flavor.image_url.trim() : '';
    if (imgUrl && imgUrl.indexOf('https://') === 0) {
      var img = document.createElement('img');
      img.className = 'whero-flavor-card__photo';
      img.src = imgUrl;
      img.alt = flavor.name + ' ice cream';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 800;
      img.height = 800;
      imgWrap.appendChild(img);
    } else {
      var ph = document.createElement('div');
      ph.className = 'whero-flavor-card__photo whero-flavor-card__photo--placeholder';
      ph.setAttribute('aria-hidden', 'true');
      var initial = document.createElement('span');
      initial.textContent = (flavor.name.charAt(0) || '').toUpperCase();
      ph.appendChild(initial);
      imgWrap.appendChild(ph);
    }
    article.appendChild(imgWrap);

    var body = document.createElement('div');
    body.className = 'whero-flavor-card__body';

    var h3 = document.createElement('h3');
    h3.className = 'whero-flavor-card__name';
    h3.textContent = flavor.name;
    body.appendChild(h3);

    var desc = typeof flavor.description === 'string' ? flavor.description.trim() : '';
    if (desc) {
      // Render the FULL description. The shared .whero-flavor-card__desc CSS
      // clamps it to 3 lines so cards stay aligned; the Read more toggle
      // below lifts the clamp in place. (Previously this truncated to 100
      // chars AND got CSS-clamped, so longer flavors were unreadable.)
      var descId = 'whero-vote-desc-' + Math.random().toString(36).slice(2, 8);
      var p = document.createElement('p');
      p.className = 'whero-flavor-card__desc';
      p.id = descId;
      p.textContent = desc;
      body.appendChild(p);

      // Read more / Read less. Hidden until renderCards confirms post-mount
      // that the text actually overflows the clamp — a detached node can't be
      // measured here, and a toggle on a fully-visible description is a no-op.
      var more = document.createElement('button');
      more.type = 'button';
      more.className = 'whero-flavor-card__readmore';
      more.hidden = true;
      more.setAttribute('aria-expanded', 'false');
      more.setAttribute('aria-controls', descId);
      more.textContent = 'Read more ↓';
      more.addEventListener('click', function () {
        var expanded = p.classList.toggle('is-expanded');
        more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        more.textContent = expanded ? 'Read less ↑' : 'Read more ↓';
      });
      body.appendChild(more);
    }

    var diet = buildDietBadges(flavor);
    if (diet) body.appendChild(diet);

    if (includeNotifyMe) {
      var notify = buildNotifyMe(flavor);
      if (notify) body.appendChild(notify);
    }

    article.appendChild(body);
    gridItem.appendChild(article);
    item.appendChild(gridItem);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'whero-btn whero-btn--secondary whero-flavor-vote__pick';
    btn.setAttribute('data-whero-vote-pick', '');
    btn.setAttribute('data-flavor-name', flavor.name);
    btn.textContent = pickLabel;
    item.appendChild(btn);

    return item;
  }
})();
